import React, { useState } from "react";
import axios from "axios";
import { Search, Loader2 } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useStatuses } from "@/context/StatusContext";
import { FIELDS, PRIORITY_META } from "@/lib/triage";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AddEvidence } from "@/components/report/AddEvidence";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function TrackReport() {
  const { tr, lang } = useLang();
  const { label: stLabel, color: stColor } = useStatuses();
  const [ref, setRef] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const track = async () => {
    if (!ref.trim()) return;
    setLoading(true);
    setResult(null);
    setNotFound(false);
    try {
      const r = await axios.get(`${API}/reports/${encodeURIComponent(ref.trim())}`);
      setResult(r.data);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-zinc-800/70 bg-[#111113] p-6">
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            data-testid="track-input"
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && track()}
            placeholder={tr.trackPh}
            className="h-11 border-zinc-800 bg-zinc-950 pl-9 font-mono text-[14px]"
          />
        </div>
        <Button
          data-testid="track-btn"
          onClick={track}
          disabled={loading}
          className="h-11 bg-blue-600 px-6 text-[14px] text-white hover:bg-blue-700"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : tr.trackBtn}
        </Button>
      </div>

      {notFound && (
        <div data-testid="track-notfound" className="mt-4 rounded-md border border-red-600/40 bg-red-600/10 px-3 py-2 text-[13px] text-red-300">
          {tr.trackNotFound}
        </div>
      )}

      {result && (
        <>
        <div data-testid="track-result" className="mt-4 grid grid-cols-2 gap-4 rounded-md border border-zinc-800 bg-zinc-950 p-4">
          <div>
            <div className="label-caps">{tr.successRef}</div>
            <div className="mt-0.5 font-mono text-[14px] text-zinc-100">{result.reference}</div>
          </div>
          <div>
            <div className="label-caps">{tr.trackStatus}</div>
            <div className="mt-0.5 inline-flex items-center gap-1.5 text-[13px]" style={{ color: stColor(result.status) }}>
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: stColor(result.status) }} /> {stLabel(result.status, lang)}
            </div>
          </div>
          <div>
            <div className="label-caps">Platform</div>
            <div className="mt-0.5 text-[13px] text-zinc-300">{result.platform}</div>
          </div>
            <div>
              <div className="label-caps">{tr.trackCreated}</div>
              <div className="mt-0.5 text-[13px] text-zinc-300">
                {new Date(result.created_at).toLocaleString()}
              </div>
            </div>
          </div>
          {result.guidelines?.length > 0 && (
            <div className="mt-4 rounded-md border border-zinc-800 bg-zinc-950 p-4" data-testid="track-guidelines">
              <div className="label-caps mb-2">{{ hu: "Vonatkozó közösségi irányelvek", en: "Applicable community guidelines", de: "Anwendbare Community-Richtlinien", fr: "Règles communautaires applicables" }[lang] || "Applicable community guidelines"}</div>
              <div className="space-y-2">
                {result.guidelines.map((g) => (
                  <div key={g.id} className="rounded-lg border border-zinc-800/70 bg-[#111113] p-3">
                    <div className="flex items-center gap-1.5">
                      <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-zinc-400">{g.platform}</span>
                      <span className="text-[13px] font-medium text-zinc-100">{g.title}</span>
                    </div>
                    {g.content && <p className="mt-1 whitespace-pre-wrap text-[12px] leading-relaxed text-zinc-400">{g.content}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {result.assessment && (
            <div className="mt-4 rounded-md border border-zinc-800 bg-zinc-950 p-4" data-testid="track-assessment">
              <div className="label-caps mb-2 flex items-center gap-2">
                {{ hu: "Az értékelés részletei", en: "Assessment details", de: "Bewertungsdetails", fr: "Détails de l'évaluation", cs: "Podrobnosti posouzení" }[lang] || "Assessment details"}
                {result.assessment.priority && PRIORITY_META[result.assessment.priority] && (
                  <span className="rounded px-1.5 py-0.5 text-[10px] font-bold text-white" style={{ backgroundColor: PRIORITY_META[result.assessment.priority].color }}>{result.assessment.priority}</span>
                )}
              </div>
              <div className="space-y-2">
                {FIELDS.filter((f) => result.assessment[f.key] && (Array.isArray(result.assessment[f.key]) ? result.assessment[f.key].length : true)).map((f) => (
                  <div key={f.key} className="rounded-lg border border-zinc-800/70 bg-[#111113] p-3">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{f.title}</div>
                    <div className="mt-0.5 text-[13px] text-zinc-200">{Array.isArray(result.assessment[f.key]) ? result.assessment[f.key].join(", ") : result.assessment[f.key]}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <AddEvidence reference={result.reference} />
        </>
      )}
    </div>
  );
}
